/**
 * The History of a database (UD-LOG): who changed what, newest first, in cursor pages. What the
 * server returns follows the viewer's current rights (contents need `database.view`; an
 * administrator of access only gets ids and roles) - the client never filters for security.
 */
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

export type HistoryCategory = 'records' | 'structure' | 'views' | 'database' | 'access' | 'export';

export const HISTORY_CATEGORIES: readonly HistoryCategory[] = [
  'records',
  'structure',
  'views',
  'database',
  'access',
  'export',
];

export interface HistoryActor {
  id: string | null;
  name: string | null;
}

export interface HistoryEvent {
  id: string;
  event_type: string;
  category: HistoryCategory;
  table_id: string | null;
  record_id: string | null;
  column_id: string | null;
  operation_id: string | null;
  actor: HistoryActor;
  subject: HistoryActor | null;
  /** Shape depends on `event_type` and `schema_version`; read defensively. */
  payload: Record<string, unknown>;
  schema_version: number;
  created_at: string;
}

export interface HistoryPage {
  items: HistoryEvent[];
  next_cursor: string | null;
  /** Names of the users that payloads refer to (previous / new owner). */
  users: Record<string, string>;
}

export interface HistoryFilters {
  categories: readonly HistoryCategory[];
  tableId: string | null;
  actorId: string | null;
  /** ISO instants (UTC). */
  since: string | null;
  until: string | null;
}

export const NO_HISTORY_FILTERS: HistoryFilters = {
  categories: [],
  tableId: null,
  actorId: null,
  since: null,
  until: null,
};

const PAGE_SIZE = 30;

function params(filters: HistoryFilters): string {
  const query = new URLSearchParams();
  for (const category of filters.categories) query.append('category', category);
  if (filters.tableId) query.set('table_id', filters.tableId);
  if (filters.actorId) query.set('actor_id', filters.actorId);
  if (filters.since) query.set('since', filters.since);
  if (filters.until) query.set('until', filters.until);
  return query.toString();
}

type Pages = InfiniteData<HistoryPage, string | null>;

function page(path: string, filter: string, cursor: string | null, signal: AbortSignal) {
  const query = [filter, `limit=${String(PAGE_SIZE)}`, cursor ? `cursor=${cursor}` : '']
    .filter(Boolean)
    .join('&');
  return api.get<HistoryPage>(`${path}?${query}`, { signal });
}

/** The database's History. Refreshed by realtime hints while open (no polling). */
export function useDatabaseHistory(databaseId: string, filters: HistoryFilters, enabled = true) {
  return useInfiniteQuery<HistoryPage, Error, Pages, readonly unknown[], string | null>({
    queryKey: queryKeys.databases.history(databaseId, filters),
    queryFn: ({ pageParam, signal }) =>
      page(`/v1/databases/${databaseId}/history`, params(filters), pageParam, signal),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

/** One record's events (the record form). */
export function useRecordHistory(recordId: string, enabled = true) {
  return useInfiniteQuery<HistoryPage, Error, Pages, readonly unknown[], string | null>({
    queryKey: queryKeys.databases.recordHistory(recordId),
    queryFn: ({ pageParam, signal }) =>
      page(`/v1/database-records/${recordId}/history`, '', pageParam, signal),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

/** A chunk of the changes of a bulk entry (a big formatting, an import, a copy). */
export interface HistoryDetailItem {
  record_id: string;
  /** `format` entries */
  column_id?: string | null;
  property?: string;
  before?: unknown;
  after?: unknown;
  /** `records` entries: the values created, by column id */
  cells?: Record<string, unknown>;
  formatting?: Record<string, unknown> | null;
}

export interface HistoryDetailColumn {
  id: string;
  name: string;
  field_type?: string;
  /** option id -> label, as it was */
  options?: Record<string, string>;
}

export interface HistoryDetailPage {
  kind: 'format' | 'records';
  items: HistoryDetailItem[];
  next_cursor: string | null;
  columns: HistoryDetailColumn[];
  users: Record<string, string>;
}

/** The stored changes of a bulk History entry, a few chunks per page (optionally one record's). */
export function useHistoryDetails(
  databaseId: string,
  eventId: string,
  recordId: string | null = null,
) {
  return useInfiniteQuery<
    HistoryDetailPage,
    Error,
    InfiniteData<HistoryDetailPage, string | null>,
    readonly unknown[],
    string | null
  >({
    // stored chunks never change: not part of the History queries that hints refresh
    queryKey: ['databases', 'history-details', databaseId, eventId, recordId],
    queryFn: ({ pageParam, signal }) => {
      const query = new URLSearchParams({ chunks: '1' });
      if (pageParam) query.set('cursor', pageParam);
      if (recordId) query.set('record_id', recordId);
      return api.get<HistoryDetailPage>(
        `/v1/databases/${databaseId}/history/${eventId}/details?${query.toString()}`,
        { signal },
      );
    },
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor,
  });
}
